import React, { useEffect, useState } from 'react';
import { Box, Button, ClickAwayListener, MenuItem, MenuList, useMediaQuery, useTheme } from '@mui/material';
import { MenuDialogStyles } from '../styles/menuDialog';
import { useAppSelector } from '../../store/hook';
import localizeContentMapper from '../../common/utils/localizeContentMapper';
import { WorkflowItemCode } from '../../constants';
import { generalContentDefaultConfiguration } from '../../generalContent/constants';
import { WorkflowItem } from '../../store/interfaces';
import { CloseOutlined, MoreVertTwoTone } from '@mui/icons-material';
import { ANIMATION_OUT_TIME } from '../../constants/timers';

const menuStyle = { position: 'absolute', top: '5rem' };

interface MenuItemsProps {
  onClose?: () => void;
  className?: string;
  id?: string;
}

const MenuDialog = (): React.JSX.Element => {
  const [menuStyles, setMenuStyles] = useState<{ position: string; top: string }>(menuStyle);
  const { classes } = MenuDialogStyles({});
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    if (isDesktop) {
      window.onscroll = (): void => {
        const bannerHeight = document.getElementsByClassName('banner-section');
        if (window.pageYOffset >= bannerHeight[0]?.clientHeight + 40)
          setMenuStyles({ ...menuStyle, position: 'fixed' });
        else setMenuStyles({ ...menuStyle });
      };
    }
  });

  return isDesktop ? (
    <Box
      zIndex="modal"
      display="flex"
      top={menuStyles.top}
      style={{ top: menuStyles.top }}
      className={`${classes.menuDialogWrapper} ${menuStyles.position == 'fixed' ? classes.boxPositionFixed : classes.boxPositionAbsolute}`}>
      <MenuItems />
    </Box>
  ) : (
    <MobileMenu />
  );
};

const MenuItems = (props: MenuItemsProps): React.JSX.Element => {
  const { nestedItems } = useAppSelector((state) => state.firstWorkflowItem);
  return (
    <MenuList className={props.className} id={props.id}>
      {nestedItems.map((item: WorkflowItem) => {
        const parsedConfig =
          item.configuration && item.code === WorkflowItemCode.GENCONTENT
            ? JSON.parse(item.configuration).configWidgetJSON
            : generalContentDefaultConfiguration;
        const parsedConfiguration = item ? localizeContentMapper(parsedConfig, item.localizedContentMap) : parsedConfig;
        return (
          <MenuItem
            data-testid={'menu-item-button'}
            key={item.id}
            disableRipple={true}
            onClick={(e: React.MouseEvent<HTMLLIElement, MouseEvent>): void => {
              e?.preventDefault();
              if (props.onClose) props.onClose();
              const sectionElem = document.getElementById(item.id);
              if (sectionElem) {
                // scroll to element top wrt body top.
                window.scrollTo({
                  top: sectionElem.getBoundingClientRect().top - document.body.getBoundingClientRect().top,
                  left: sectionElem.getBoundingClientRect().left - document.body.getBoundingClientRect().left,
                });
              }
            }}>
            {parsedConfiguration.title}
          </MenuItem>
        );
      })}
    </MenuList>
  );
};

const MobileMenu = (): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const { classes } = MenuDialogStyles({ isMenuOpen: open, isAnimating });

  const closeMenu = (): void => {
    if (!open) return;
    setIsAnimating(true);
    document.body.style.overflow = 'auto';
    setTimeout(() => {
      setOpen(false);
      setIsAnimating(false);
    }, ANIMATION_OUT_TIME);
  };

  const handleOpen = (): void => {
    if (open) closeMenu();
    else {
      /** hide background scroll */
      document.body.style.overflow = 'hidden';
      setOpen(!open);
    }
  };

  const handleClose = (): void => closeMenu();

  return (
    <Box className={classes.menuDialogWrapper}>
      <div className={classes.mobileWrapper}>
        <ClickAwayListener onClickAway={handleClose}>
          <Button data-testid={'menu-close-button'} onClick={handleOpen}>
            {open ? <CloseOutlined /> : <MoreVertTwoTone />}
          </Button>
        </ClickAwayListener>
        <MenuItems
          onClose={handleClose}
          id="animation_popup"
          className={`${classes.mobileMenuWrapper} ${open && classes.animationIn} ${
            !open && classes.defaultAnimation
          } ${isAnimating && classes.animationOut}`}
        />
      </div>
    </Box>
  );
};

export default MenuDialog;
